import React from 'react';
import { Button, Select, useToast } from '@crisellevaldez/speculo-ui';
import { cleanPhoneNumber } from '../../../../utils/cleanPhoneNumber';
import { Pencil, Trash, UserPlus } from 'lucide-react';
import { Flow } from '../../../../hooks/useFlowsData';
import { deleteFlow } from '../../../../api/remiApi';
import { testCall } from '../../../../api/flowWorker';
import { useApolloClient } from '@apollo/client/react';
import { ConfirmationDialog } from '../../../common/dialogs';
import { useCallTesters } from '../../../../hooks/flows/useCallTesters';
import { AddTestContactModal, TestContactInput } from '../AddTestContactModal';
import { useOrganizationStore } from '../../../../stores/useOrganizationStore';
import { useMLSSubscriptionStatus } from '../../../../hooks/client/mls/useMLSSubscriptionStatus';

interface FlowActionsProps {
  flow: Flow;
  organizationId: string;
  currentDescription?: string;
  visibleButtons: {
    update: boolean;
    schedule: boolean;
    delete: boolean;
    testCall: boolean;
    pause: boolean;
    resume: boolean;
  };
  onClose: () => void;
  onUpdate: () => void;
  onSchedule?: () => void;
  onPause?: () => void;
  onResume?: () => void;
  isUpdating: boolean;
  isScheduling: boolean;
  isPausing?: boolean;
  isResuming?: boolean;
  disabled?: {
    update?: boolean;
    schedule?: boolean;
    pause?: boolean;
    resume?: boolean;
    testCall?: boolean;
  };
  /** Callback when test contact selection changes */
  onTestContactChange?: (contactId: string | null) => void;
  /** Callback when add test contact modal opens/closes */
  onAddTestContactModalChange?: (isOpen: boolean) => void;
  /** When false (agents), the mutating actions (update/schedule/pause/
   *  resume/delete) are disabled; testing a flow stays available. */
  canEdit?: boolean;
}

export const FlowActions: React.FC<FlowActionsProps> = ({
  flow,
  organizationId,
  currentDescription,
  visibleButtons,
  onClose,
  onUpdate,
  onSchedule,
  onPause,
  onResume,
  isUpdating,
  isScheduling,
  isPausing,
  isResuming,
  disabled,
  onTestContactChange,
  onAddTestContactModalChange,
  canEdit = true,
}) => {
  const { showToast } = useToast();
  const apolloClient = useApolloClient();
  const { organization } = useOrganizationStore();
  const { canScheduleFlows, pendingMLS } = useMLSSubscriptionStatus(organization);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [selectedTestFlow, setSelectedTestFlow] = React.useState<string>('');
  const [selectedTestNumber, setSelectedTestNumber] = React.useState<string>('');
  const [isTesting, setIsTesting] = React.useState(false);
  const {
    callTesters,
    loading: loadingCallTesters,
    addCallTester,
    isAddingCallTester,
  } = useCallTesters(true);

  // Add test contact modal state
  const [showAddTestContactModal, setShowAddTestContactModal] = React.useState(false);

  const testNumberError = (() => {
    if (!flow.transfer_numbers || !selectedTestNumber) return undefined;

    const cleanedTestNumber = cleanPhoneNumber(selectedTestNumber);

    // Find the name and number that matches
    const matchingEntry = Object.entries(flow.transfer_numbers).find(
      ([_, number]) => cleanPhoneNumber(number) === cleanedTestNumber
    );

    if (matchingEntry) {
      const [name, number] = matchingEntry;
      return `Test number cannot be the same as transfer line ${name} (${number})`;
    }

    return undefined;
  })();

  const handleDelete = async () => {
    if (!flow?.id) return;

    setIsDeleting(true);
    try {
      await deleteFlow(organizationId, flow.id);

      showToast({
        message: 'Flow deleted successfully',
        variant: 'success',
      });
      apolloClient.refetchQueries({ include: ['GetFlows'] });
      setShowDeleteModal(false);
      onClose();
    } catch (error) {
      showToast({
        message: error instanceof Error ? error.message : 'Failed to delete flow',
        variant: 'error',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAddTestContact = async (data: TestContactInput) => {
    const newContactId = await addCallTester(data);
    showToast({
      message: 'Test contact added successfully',
      variant: 'success',
    });
    // Auto-select the newly created test contact
    if (newContactId) {
      setSelectedTestFlow(newContactId);
      setSelectedTestNumber(data.phoneNumber);
    }
    setShowAddTestContactModal(false);
    // Notify parent that the add test contact modal is closing
    onAddTestContactModalChange?.(false);
  };

  const handleTestCall = async () => {
    if (!flow?.id || !selectedTestFlow) {
      showToast({
        message: 'Please select a test contact',
        variant: 'error',
      });
      return;
    }

    try {
      setIsTesting(true);
      await testCall({
        contactId: selectedTestFlow,
        flowId: flow.id,
        organizationId,
        reasonForCall: currentDescription?.trim() || flow.description,
      });
      showToast({
        message: 'Test call initiated successfully',
        variant: 'success',
      });
    } catch (error) {
      showToast({
        message: error instanceof Error ? error.message : 'Failed to initiate test call',
        variant: 'error',
      });
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <>
      <div className="flex flex-col w-full gap-2">
        <div className="flex justify-between items-end">
          <div className="flex w-3/5 flex-col gap-2" data-tour="test-call-section">
            {testNumberError && (
              <div className="text-red-500 text-sm w-full">{testNumberError}</div>
            )}
            <div className="flex items-center gap-2">
              <Select
                placeholder="Select test flow contact"
                options={callTesters}
                className="w-full"
                data-tour="test-contact-select"
                onChange={(value) => {
                  if (typeof value === 'string') {
                    setSelectedTestFlow(value);
                    const selectedContact = callTesters.find((tester) => tester.value === value);
                    if (selectedContact) {
                      setSelectedTestNumber(selectedContact.phoneNumber);
                    }
                    // Notify parent of selection change
                    onTestContactChange?.(value || null);
                  }
                }}
                value={selectedTestFlow}
                isLoading={loadingCallTesters}
                searchable
              />
              <Button
                variant="outline"
                size="sm"
                className="p-2"
                onClick={() => {
                  setShowAddTestContactModal(true);
                  onAddTestContactModalChange?.(true);
                }}
                title="Add Test Contact"
                data-tour="add-test-contact-button"
              >
                <UserPlus className="w-4 h-4" />
              </Button>
              {visibleButtons.testCall && (
                <div className="relative" data-tour="test-call-button">
                  <Button
                    variant="outline"
                    size="sm"
                    className="px-3 py-2 whitespace-nowrap min-w-[100px]"
                    onClick={handleTestCall}
                    disabled={
                      !selectedTestFlow ||
                      isTesting ||
                      disabled?.testCall ||
                      Boolean(testNumberError)
                    }
                    isLoading={isTesting}
                    title={
                      testNumberError
                        ? 'Test number cannot be the same as a transfer line'
                        : undefined
                    }
                  >
                    Test Call
                  </Button>
                </div>
              )}
            </div>
          </div>
          <div className="flex w-2/5 justify-end align-bottom">
            <Button
              variant="secondary"
              className="bg-red-600 text-white hover:bg-red-700 flex items-center gap-2"
              onClick={() => setShowDeleteModal(true)}
              disabled={isDeleting || !visibleButtons.delete || !canEdit}
              title={!canEdit ? 'Only your organization admin can delete flows' : undefined}
            >
              <Trash className="w-4 h-4" />
              Delete
            </Button>
          </div>
        </div>
        <div className="flex justify-between items-center">
          <div className="flex gap-2">
            {visibleButtons.schedule && (
              <Button
                variant="primary"
                onClick={() => {
                  // Defensive guard mirroring the disabled state, so scheduling
                  // can't fire if the button is ever re-enabled out from under it.
                  if (!canScheduleFlows) return;
                  onSchedule?.();
                }}
                disabled={isScheduling || disabled?.schedule || !canScheduleFlows || !canEdit}
                isLoading={isScheduling}
                title={
                  !canScheduleFlows ? `MLS approval pending: ${pendingMLS.join(', ')}` : undefined
                }
              >
                Schedule
              </Button>
            )}
            {visibleButtons.pause && (
              <Button
                variant="outline"
                onClick={onPause}
                disabled={isPausing || disabled?.pause || !canEdit}
                isLoading={isPausing}
              >
                Pause
              </Button>
            )}
            {visibleButtons.resume && (
              <Button
                variant="outline"
                onClick={onResume}
                disabled={isResuming || disabled?.resume || !canEdit}
              >
                {isResuming ? 'Scheduling…' : 'Resume'}
              </Button>
            )}
          </div>
          <Button
            onClick={onUpdate}
            disabled={isUpdating || disabled?.update || !visibleButtons.update || !canEdit}
            isLoading={isUpdating}
            className="items-center gap-2 px-[29px] h-8"
            title={!canEdit ? 'Only your organization admin can update flows' : undefined}
          >
            <Pencil className="w-4 h-4" />
            Update
          </Button>
        </div>
      </div>

      <ConfirmationDialog
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete Flow"
        message={`Are you sure you want to delete the flow "${flow.name}"?`}
        confirmLabel="Delete"
        isLoading={isDeleting}
        variant="danger"
      />

      {/* Add Test Contact Modal */}
      <AddTestContactModal
        isOpen={showAddTestContactModal}
        onClose={() => {
          setShowAddTestContactModal(false);
          onAddTestContactModalChange?.(false);
        }}
        onSubmit={handleAddTestContact}
        isSubmitting={isAddingCallTester}
      />
    </>
  );
};
